import { CHANGE_V2_CURRENT_DATE } from '../constants';
import {
  CHANGE_CATEGORY,
  CHANGE_STATUS,
  CRITICALITY,
  LIFECYCLE_STEP,
  SCOPE_TYPE,
  TChangeCategory,
  TChangeStatus,
  TChangeV2,
  TCriticality,
  TLifecycleStep,
  TScopeType,
} from '../models';
import { createBlankChange } from '../utils/factory';

// Author-friendly, flat shape for seeding the V2 dashboard register from the org
// mock config. Mirrors `organization.mock.changeV2.changes` (see
// organizations.types.ts). A confideck developer authors these; the mapper below
// fills in the rest of the connected Change record with sensible defaults.
export type TChangeV2ConfigEntry = {
  displayId?: string;
  title: string;
  status: string;
  changeManager?: string;
  initiator?: string;
  currentOwner?: string;
  scopeType?: string;
  criticality?: string;
  changeCategory?: string;
  progress?: number;
  requestDate?: string;
  targetDate?: string;
  nextAction?: string;
  affectedProducts?: string[];
  affectedProductFamilies?: string[];
  affectedQmsAreas?: string[];
  blockers?: string[];
  awaitingExternalDecision?: boolean;
};

// Matches a config string against the values of a stable enum object, returning
// the canonical value or undefined so an unexpected config value degrades
// gracefully instead of corrupting the record.
const matchEnum = <T extends string>(values: readonly T[], value: string | undefined): T | undefined =>
  value ? values.find((candidate) => candidate.toLocaleLowerCase() === value.toLocaleLowerCase()) : undefined;

const STATUS_TO_LIFECYCLE: Record<TChangeStatus, TLifecycleStep> = {
  [CHANGE_STATUS.DRAFT]: LIFECYCLE_STEP.REQUEST,
  [CHANGE_STATUS.SUBMITTED]: LIFECYCLE_STEP.REQUEST,
  [CHANGE_STATUS.IN_ASSESSMENT]: LIFECYCLE_STEP.ASSESSMENT,
  [CHANGE_STATUS.INITIAL_PLANNING]: LIFECYCLE_STEP.PLANNING,
  [CHANGE_STATUS.IN_EXECUTION]: LIFECYCLE_STEP.EXECUTION,
  [CHANGE_STATUS.IN_COMPLETION]: LIFECYCLE_STEP.COMPLETION,
  [CHANGE_STATUS.CLOSED]: LIFECYCLE_STEP.COMPLETION,
  [CHANGE_STATUS.CANCELLED]: LIFECYCLE_STEP.REQUEST,
};

const toIsoDate = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  // Accept plain "YYYY-MM-DD" as well as full ISO strings.
  return value.includes('T') ? value : `${value}T00:00:00.000Z`;
};

const slugifyId = (entry: TChangeV2ConfigEntry, index: number): string =>
  (entry.displayId ?? `cfg-change-${index + 1}`).toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-');

// Maps one config entry onto a full connected Change record, defaulting every
// field the author did not provide via `createBlankChange`.
export const mapConfigChange = (entry: TChangeV2ConfigEntry, index: number): TChangeV2 => {
  const status = matchEnum(Object.values(CHANGE_STATUS), entry.status) ?? CHANGE_STATUS.DRAFT;
  const initiator = entry.initiator ?? entry.changeManager ?? 'Unassigned';
  const lifecycleStep = STATUS_TO_LIFECYCLE[status];

  return createBlankChange(initiator, {
    id: slugifyId(entry, index),
    displayId: entry.displayId,
    title: entry.title,
    status,
    lifecycleStep,
    changeManager: entry.changeManager,
    currentOwner: entry.currentOwner ?? entry.changeManager ?? initiator,
    scopeType: matchEnum<TScopeType>(Object.values(SCOPE_TYPE), entry.scopeType),
    criticality: matchEnum<TCriticality>(Object.values(CRITICALITY), entry.criticality),
    changeCategory: matchEnum<TChangeCategory>(Object.values(CHANGE_CATEGORY), entry.changeCategory),
    progress: typeof entry.progress === 'number' ? Math.max(0, Math.min(100, entry.progress)) : 0,
    requestDate: toIsoDate(entry.requestDate) ?? CHANGE_V2_CURRENT_DATE.toISOString(),
    targetDate: toIsoDate(entry.targetDate),
    nextAction: entry.nextAction,
    affectedProducts: entry.affectedProducts ?? [],
    affectedProductFamilies: entry.affectedProductFamilies ?? [],
    affectedQmsAreas: entry.affectedQmsAreas ?? [],
    blockers: entry.blockers ?? [],
    awaitingExternalDecision: entry.awaitingExternalDecision ?? false,
  });
};

export const mapConfigChanges = (entries: TChangeV2ConfigEntry[] | undefined): TChangeV2[] | undefined =>
  entries && entries.length > 0 ? entries.map(mapConfigChange) : undefined;
